import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Divider,
  Card,
  CardMedia,
  CardContent,
  CircularProgress
} from '@mui/material';
import { useQuery } from '@tanstack/react-query'; 
import DOMPurify from 'dompurify'; 

import 'bootstrap/dist/css/bootstrap.min.css';
import axiosClient from 'axiosClient'; 

// --- HELPER COMPONENT ---
// Handles the image fade logic for each individual milestone
const MilestoneCard = ({ item }) => {
  const [imgLoaded, setImgLoaded] = useState(false);

  return (
    <Card
      sx={{
        borderRadius: "5px",
        width: "100%",
        boxShadow: "0 0px 30px rgba(0,0,0,0.30)",
      }}
    >
      {/* IMAGE CONTAINER */}
      <Box sx={{ position: "relative", height: 300, backgroundColor: "#f5f5f5" }}>
        
        {/*  SPINNER (Image specific) */}
        {!imgLoaded && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1,
            }}
          >
            <CircularProgress size={40} sx={{ color: "#222843" }} />
          </Box>
        )}

        {/* 2. IMAGE WITH FADE */}
        <CardMedia
          component="img"
          image={item.image} 
          alt={item.title}
          onLoad={() => setImgLoaded(true)}
          sx={{
            height: "100%",
            width: "100%",
            objectFit: "cover",
            position: "relative",
            zIndex: 2,
            opacity: imgLoaded ? 1 : 0, 
            transition: "opacity 0.8s ease-in-out",
          }}
        />
      </Box>

      <CardContent sx={{ minHeight: 140 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            color: "#222843",
            mb: 1,
            borderBottom: "1px solid #e0e0e0",
            pb: 1,
          }}
        >
          {item.title}
        </Typography>

        {/* 2. CRITICAL FIX: Render the Tiptap HTML cleanly and securely */}
        <Typography
          component="div" // Changed from p to div so nested <p> tags don't cause HTML errors
          color="text.secondary"
          sx={{ 
            lineHeight: 1.5, 
            fontSize: 16,
            // Clean up the default margins that Tiptap <p> tags bring in
            '& p': { margin: 0, mb: 1 },
            '& p:last-of-type': { mb: 0 },
            '& ul': { margin: 0, paddingLeft: 2.5 },
            '& ol': { margin: 0, paddingLeft: 2.5 },
          }}
          dangerouslySetInnerHTML={{
            // item.desc or item.description depending on your exact API mapping
            __html: DOMPurify.sanitize(item.desc || item.description || "No description provided.")
          }}
        />
      </CardContent>
    </Card>
  );
};

const Milestone = () => {
  // --- DATA FETCHING (React Query) ---
  const { data: timelineData = [], isLoading: loading } = useQuery({
    queryKey: ['milestones'], // Unique key for caching
    queryFn: async () => {
      const response = await axiosClient.get('/milestones');
      return response.data.data || [];
    },
  });

  // --- LOADING STATE ---
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {/* Header section */}
      <Box sx={{ textAlign: "center", py: 4, bgcolor: '#f8f7f7ff', }}>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 800,
            letterSpacing: 1,
            textTransform: "uppercase",
            color: "#1a202c",
          }}
        >
          MILESTONES
        </Typography>
        <Typography
          variant="h5"
          sx={{
            color: "#6b7280",
            fontWeight: 500,
            mt: 0.5,
          }}
        >
          Home / Milestones
        </Typography>
      </Box>
      <Box
        sx={{
          width: "100vw",
          backgroundColor: '#ffffff',
          py: 5,
          boxShadow: "0 -2px 6px rgba(0, 0, 0, 0.15)",
        }}
      >
        <Container maxWidth="lg">
          {/* Header inside timeline */}
          <Box display="flex" alignItems="center" mb={4}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                whiteSpace: "nowrap",
                mr: 2,
                ml: { xl: -10, lg: 'auto' }
              }}
            >
              Centrum Fuel's Early Years
            </Typography>
            <Divider sx={{ flexGrow: 1, borderColor: "#b9b9b9" }} />
          </Box>

          {/* DYNAMIC TIMELINE LOOP */}
          {timelineData.length > 0 ? (
            timelineData.map((section, i) => (
              <Box key={i} mb={6}>
                <Grid container spacing={3} alignItems="flex-start">

                  {/* LEFT SIDE — DECADE LABEL */}
                  <Grid item xs={12} md={2}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 850,
                        color: "#22284340",
                        ml: { xl: -10, lg: 'auto' }
                      }}
                    >
                      {section.decade}
                    </Typography>
                  </Grid>

                  {/* RIGHT SIDE — CARDS */}
                  <Grid item xs={12} md={10}>
                    <Grid container spacing={3}>
                      {section.events?.map((item, index) => (
                        <Grid item xs={12} sm={6} key={index}>
                          {/* Use the Helper Component */}
                          <MilestoneCard item={item} />
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                </Grid>

                {/* Divider under decade row */}
                <Divider sx={{ mt: 3, borderColor: "#b9b9b9" }} />
              </Box>
            ))
          ) : (
             <Typography textAlign="center" variant="h6" color="text.secondary">
               No milestones found.
             </Typography>
          )}

        </Container>
      </Box>
    </>
  );
};

export default Milestone;